import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { getMemberFinancialOverview, type FinancialOverview } from "../api/payments";

interface MemberFinancialPanelProps {
  accessToken: string;
  memberId: string;
  onSessionExpired(): void;
}

type InvoiceItem = FinancialOverview["invoices"][number];

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  overdue: "Em atraso",
  paid: "Pago",
  cancelled: "Cancelada",
};

function formatInvoiceStatus(status: string): string {
  return STATUS_LABELS[status] ?? "Indisponível";
}

function InvoiceRow({ invoice }: { invoice: InvoiceItem }) {
  const kindLabel = invoice.kind === "monthly_fee" ? "Mensalidade" : "Doação";
  const compLabel = invoice.competencia?.slice(0, 7) ?? "Registro avulso";
  const amountStr = Number(invoice.amount).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const statusStr = formatInvoiceStatus(invoice.status);
  const dueStr = new Date(`${invoice.due_date}T12:00:00`).toLocaleDateString("pt-BR");

  return (
    <li className="member-financial-panel__item">
      <div>
        <strong>{kindLabel}</strong>
        <span>{compLabel}</span>
      </div>
      <div className="member-financial-panel__value">
        <strong>{amountStr}</strong>
        <span>{statusStr} · vence em {dueStr}</span>
      </div>
    </li>
  );
}

function InvoicesContent({ overview }: { overview: FinancialOverview }) {
  const invoices = overview.invoices ?? [];
  if (invoices.length === 0) {
    return <p className="member-financial-panel__empty">Nenhuma cobrança registrada para este associado.</p>;
  }

  return (
    <ul className="member-financial-panel__list">
      {invoices.slice(0, 3).map((invoice) => (
        <InvoiceRow key={invoice.id} invoice={invoice} />
      ))}
    </ul>
  );
}

export function MemberFinancialPanel({ accessToken, memberId, onSessionExpired }: MemberFinancialPanelProps) {
  const [overview, setOverview] = useState<FinancialOverview | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOverview(null);
    setError(false);

    void getMemberFinancialOverview(accessToken, memberId)
      .then((next) => {
        if (!cancelled) setOverview(next);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.status === 401) {
          onSessionExpired();
          return;
        }
        setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, memberId, onSessionExpired]);

  return (
    <section className="member-financial-panel" aria-labelledby="member-financial-title">
      <div className="member-financial-panel__heading">
        <div>
          <p className="members-page__eyebrow">Acompanhamento financeiro</p>
          <h2 id="member-financial-title">Situação financeira</h2>
          <p>Resumo institucional. Operações são realizadas no atendimento dedicado.</p>
        </div>
        {overview && !error && (
          <Link className="btn btn--secondary" to={`/associados/${memberId}/financeiro`}>
            Abrir atendimento financeiro
          </Link>
        )}
      </div>
      {error && (
        <div className="status-banner status-banner--error" role="alert">
          Não foi possível consultar o financeiro. Verifique sua conexão e permissão.
        </div>
      )}
      {!overview && !error && <p role="status" aria-live="polite">Carregando situação financeira…</p>}
      {overview && !error && <InvoicesContent overview={overview} />}
    </section>
  );
}
